/**
 * Fuente ÚNICA de contactos (personas) para todos los selectores de la app.
 *
 * El maestro de PERSONAS es `contacts` (≠ `end_clients`, que son cuentas/empresas
 * de la "Cartera por cliente"). Toda pantalla que elija una persona — CRM
 * "Actividades", "Nuevo lead", agenda/historial de Boutique — debe leer de aquí,
 * NO de estado local efímero. Esto evita el bug recurrente del dropdown vacío.
 *
 * Por qué un provider y no un fetch por componente:
 *   - Un solo GET /contacts cacheado y compartido (no N fetches).
 *   - Crear un contacto en CUALQUIER módulo (`createContact`) lo deja disponible
 *     en TODOS los selectores al instante — sin re-registro por módulo.
 *
 * Notas de contrato con el backend (ContactQueryDto):
 *   - `pageSize` tiene MAX 100 (un pageSize=500 da 400 → lista vacía: ese fue el
 *     bug de la hidratación previa). Paginamos de a 100 hasta traer todo.
 *   - `sortBy` ∈ {created_at, first_name, last_name, email, company} (snake_case).
 *     `firstName` NO es válido (400). Usamos `first_name`.
 *   - GET /contacts exige el permiso `contacts:view`; un 403 deja la lista vacía
 *     con `error='forbidden'` (no rompemos la UI).
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { api, ApiError } from '../lib/api';

/** Contacto crudo tal como lo devuelve GET /contacts. */
export interface RawContact {
  id: string;
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  jobTitle?: string | null;
}

/** Forma normalizada que consumen los selectores (incluye `name` ya compuesto). */
export interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  /** "First Last" listo para mostrar (o email si no hay nombre). */
  name: string;
  company: string;
  email: string;
  phone: string;
  jobTitle: string;
}

interface Paginated<T> { data: T[]; total: number; page: number; pageSize: number; }

/** Body de creación/edición (espejo de CreateContactDto del backend). */
export interface ContactInput {
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
  company?: string;
  jobTitle?: string;
}

export function normalizeContact(c: RawContact): Contact {
  const firstName = (c.firstName ?? '').trim();
  const lastName = (c.lastName ?? '').trim();
  const name = `${firstName} ${lastName}`.trim() || (c.email ?? '').trim() || '(sin nombre)';
  return {
    id: c.id,
    firstName,
    lastName,
    name,
    company: c.company ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    jobTitle: c.jobTitle ?? '',
  };
}

interface ContactsCtx {
  contacts: Contact[];
  loading: boolean;
  /** null = ok; 'forbidden' = falta permiso contacts:view; 'error' = otro fallo. */
  error: null | 'forbidden' | 'error';
  refresh: () => Promise<void>;
  /** POST /contacts → persiste, refresca la cache y devuelve el contacto creado. */
  createContact: (input: ContactInput) => Promise<Contact>;
  /** PUT /contacts/:id → persiste y refresca la cache. */
  updateContact: (id: string, input: ContactInput) => Promise<Contact>;
}

const Ctx = createContext<ContactsCtx | null>(null);

const PAGE_SIZE = 100; // máximo permitido por ContactQueryDto
const MAX_PAGES = 20;  // backstop: hasta 2000 contactos en el dropdown

/** Trae TODOS los contactos del tenant paginando de a 100 (params válidos). */
async function fetchAllContacts(): Promise<RawContact[]> {
  const all: RawContact[] = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const qs = `page=${page}&pageSize=${PAGE_SIZE}&sortBy=first_name&sortOrder=asc`;
    const res = (await api(`/contacts?${qs}`)) as Paginated<RawContact>;
    const batch = res?.data ?? [];
    all.push(...batch);
    if (batch.length < PAGE_SIZE) break; // última página
  }
  return all;
}

export function ContactsProvider({ children }: { children: ReactNode }) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | 'forbidden' | 'error'>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const raw = await fetchAllContacts();
      setContacts(raw.map(normalizeContact));
      setError(null);
    } catch (e) {
      // 403 = el rol no tiene contacts:view; otros = fallo genérico. En ambos
      // casos dejamos la lista vacía sin romper los selectores.
      setError(e instanceof ApiError && e.status === 403 ? 'forbidden' : 'error');
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const createContact = useCallback(async (input: ContactInput): Promise<Contact> => {
    const created = (await api('/contacts', { method: 'POST', body: input })) as RawContact;
    const norm = normalizeContact(created);
    // Inserta optimistamente y reordena por nombre; refresh() de fondo concilia.
    setContacts((cs) => [...cs.filter((c) => c.id !== norm.id), norm]
      .sort((a, b) => a.name.localeCompare(b.name, 'es')));
    void refresh();
    return norm;
  }, [refresh]);

  const updateContact = useCallback(async (id: string, input: ContactInput): Promise<Contact> => {
    const updated = (await api(`/contacts/${id}`, { method: 'PUT', body: input })) as RawContact;
    const norm = normalizeContact(updated);
    setContacts((cs) => cs.map((c) => (c.id === id ? norm : c)));
    void refresh();
    return norm;
  }, [refresh]);

  const value = useMemo<ContactsCtx>(
    () => ({ contacts, loading, error, refresh, createContact, updateContact }),
    [contacts, loading, error, refresh, createContact, updateContact],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Hook de acceso. Lanza si se usa fuera del provider (uso intencional siempre dentro). */
export function useContacts(): ContactsCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useContacts debe usarse dentro de <ContactsProvider>');
  return ctx;
}
